import { defaultAccountCopy } from "@loot-os/api-contract";
import type { EndUser } from "@loot-os/client";
import type { AccountProfileContent } from "@loot-os/design-system/templates/account/accountContent";

import { useProfile } from "../../../../lib/profile.js";
import { useShopper } from "../../../../lib/shopper.js";
import { useTerminology } from "../../../../lib/terminology.js";

/**
 * Settings → Profile: GET /v1/me, the Loot end user the login maps onto. The username has Change
 * (or Choose) and View profile; the rest are the account's facts.
 */
export function useProfileContent(
  me: EndUser | null,
  openUsername: () => void,
): AccountProfileContent {
  const { user } = useShopper();
  const { showProfile } = useProfile();
  const copy = defaultAccountCopy(useTerminology()).profile;
  return {
    title: copy.title,
    description: copy.description,
    rows: [
      {
        id: "username",
        label: copy.rows.username,
        value: me?.username ?? copy.noUsername,
        muted: !me?.username,
        actions: [
          {
            label: me?.username ? copy.change : copy.choose,
            emphasis: me?.username ? "secondary" : "primary",
            disabled: !me,
            onClick: openUsername,
          },
          { label: copy.viewProfile, disabled: !me, onClick: () => me && showProfile(me.id) },
        ],
      },
      { id: "email", label: copy.rows.email, value: user?.email ?? "—" },
      { id: "externalId", label: copy.rows.externalId, value: me?.externalId ?? "—", mono: true },
      { id: "mode", label: copy.rows.mode, value: me?.mode ?? "—" },
      { id: "nonce", label: copy.rows.nonce, value: me ? String(me.nonce) : "—" },
      {
        id: "memberSince",
        label: copy.rows.memberSince,
        value: me ? new Date(me.createdAt).toLocaleDateString() : "—",
      },
    ],
  };
}
